/**
 * RequireAuth — route guard. Redirects unauthenticated users to /login with
 * a returnTo parameter pointing back at the current location.
 *
 * Assumes useBootstrapAuth has been called by an ancestor (typically AppLayout).
 * If the bootstrap is still loading, this component renders nothing — the
 * AppLayout shows a loading state until bootstrap resolves.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import type { ReactNode } from 'react';

interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();

  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search;
    const target = returnTo === '/' ? '/login' : `/login?returnTo=${encodeURIComponent(returnTo)}`;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
